/**
 * @fileoverview Venue Card component.
 * @summary Displays a single venue card with edit, delete, and create event actions.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/VenueCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faEdit, faTrashAlt, faCalendarPlus } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import Spinner from '../../../components/Spinner'
import type { Venue } from '../../../store/slices/venuesSlice'

interface VenueCardProps {
  venue: Venue
  isDeleting: boolean
  onEdit: (venue: Venue) => void
  onDelete: (venue: Venue) => void
}

/**
 * @summary Venue card component.
 * @param {VenueCardProps} props - Component props.
 * @returns {React.ReactElement} Venue card.
 */
export function VenueCard({
  venue,
  isDeleting,
  onEdit,
  onDelete,
}: VenueCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleCreateEvent = () => {
    navigate(`/events/create?venueId=${venue.id}`)
  }

  return (
    <div className="card">
      <div className="flex items-start gap-4">
        <div className="shrink-0 mt-1">
          <FontAwesomeIcon
            icon={faMapMarkerAlt}
            className="w-6 h-6 text-(--color-primary)"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg text-(--color-text-primary) mb-2">
            {venue.name}
          </h3>
          {venue.address && (
            <p className="text-sm text-(--color-text-secondary) mb-1">{venue.address}</p>
          )}
          <p className="text-sm text-(--color-text-secondary) mb-2">
            {venue.city}
            {venue.country && `, ${venue.country}`}
          </p>
          {venue.googleMapsUrl && (
            <a
              href={venue.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-(--color-primary-700) dark:text-(--color-primary-300) hover:underline inline-block mb-3"
            >
              {t('venues.viewOnGoogleMaps')}
            </a>
          )}
          <div className="flex  items-center gap-2 mt-3">
            <button
              onClick={handleCreateEvent}
              className="btn btn-primary btn-sm flex items-center gap-2"
              title={t('events.createEvent')}
            >
              <FontAwesomeIcon icon={faCalendarPlus} className="w-4 h-4" />
              <span>{t('events.createEvent')}</span>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 shrink-0">
          <button
            onClick={() => onEdit(venue)}
            className="btn btn-secondary btn-sm p-2"
            title={t('venues.edit')}
          >
            <FontAwesomeIcon
              icon={faEdit}
              className="w-4 h-4"
            />
          </button>
          <button
            onClick={() => onDelete(venue)}
            disabled={isDeleting}
            className="btn btn-secondary btn-sm p-2 disabled:opacity-50"
            title={t('venues.confirmDeleteTitle')}
          >
            {isDeleting ? (
              <Spinner size="sm" />
            ) : (
              <FontAwesomeIcon
                icon={faTrashAlt}
                className="w-4 h-4 text-red-500 dark:text-red-700"
              />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

